import React, { useRef, useState } from "react";
import { FileArchive, Loader2, UploadCloud, ArrowRight } from "lucide-react";
import JSZip from "jszip";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FOLDERS = [
  "[01] Manuais e Políticas de SSMA",
  "[02] PGR e PCMSO Vigentes",
  "[03] DDSs Arquivados",
  "[04] Fichas de EPI",
  "[05] Laudos Técnicos (Ruído, Calor, LTCAT)",
  "[06] Auditorias ISO 14001 / 45001",
  "[07] FISPQ / FDS",
  "[08] Permissões de Trabalho / APR",
];
const NORMS = ["ISO 14001", "ISO 45001", "ISO 9001", "NR-01", "NR-06", "NR-12", "NR-13", "Geral"];

export function BatchImport({ target = "documents", idKey = "document_id", nameKey = "document_name",
  titleKey = "title", onDone, testid = "batch-import", isoItems = null, aiAso = false, folders = null, defaultFolder = null }) {
  const folderList = folders && folders.length ? folders : FOLDERS;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [folder, setFolder] = useState(defaultFolder || folderList[0]);
  const [norm, setNorm] = useState("ISO 14001");
  const [entries, setEntries] = useState([]);
  const [mapping, setMapping] = useState({});
  const [pending, setPending] = useState(null);
  const inputRef = useRef(null);
  const isDoc = target === "documents";
  const isIso = target === "iso14001";
  const mapMode = isIso && Array.isArray(isoItems) && isoItems.length > 0;

  const reset = () => { setEntries([]); setMapping({}); setPending(null); setProgress(""); };

  const doAsoImport = async (file) => {
    setBusy(true); setProgress("Enviando documentos...");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/ai-zip/${target}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      const jobId = data.job_id;
      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: st } = await api.get(`/ai-zip-status/${jobId}`);
          setProgress(`Lendo documentos com IA: ${st.processed}/${st.total}...`);
          if (st.status === "done") {
            done = true;
            toast.success(`${st.imported} registro(s) lidos e importados${st.failed ? ` · ${st.failed} sem leitura automática` : ""}`);
          }
        } catch { done = true; }
      }
      setOpen(false); reset(); onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na importação");
    } finally { setBusy(false); setProgress(""); }
  };

  const doUpload = async (file, map) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("target", target);
      fd.append("folder", folder);
      fd.append("norm", norm);
      fd.append("id_key", idKey);
      fd.append("name_key", nameKey);
      fd.append("title_key", titleKey);
      if (map && Object.keys(map).length) fd.append("mapping", JSON.stringify(map));
      const { data } = await api.post("/files/upload-zip", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      toast.success(`${data.imported} arquivo(s) importado(s)${data.skipped ? ` · ${data.skipped} ignorado(s)` : ""}`);
      setOpen(false); reset();
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na importação");
    } finally { setBusy(false); }
  };

  const handleZip = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Selecione um arquivo .zip"); return; }
    if (aiAso) { doAsoImport(file); return; }
    if (mapMode) {
      setBusy(true);
      try {
        const zip = await JSZip.loadAsync(file);
        const names = [];
        zip.forEach((rel, entry) => {
          const n = rel.split("/").pop();
          if (!entry.dir && n && !n.startsWith(".") && !rel.includes("__MACOSX")) names.push(n);
        });
        if (!names.length) { toast.error("O ZIP não contém arquivos válidos"); return; }
        setEntries(names); setPending(file); setMapping({});
      } catch { toast.error("Não foi possível ler o ZIP"); } finally { setBusy(false); }
      return;
    }
    doUpload(file, null);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { reset(); setFolder(defaultFolder || folderList[0]); setOpen(true); }}
        className="border-[#134074]/30 text-[#134074] hover:bg-[#134074]/5" data-testid={`${testid}-btn`}>
        <FileArchive className="mr-1.5 h-4 w-4" /> Importar ZIP
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle className="text-[#0B2545]">Importar arquivos em lote (ZIP)</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            {aiAso ? (
              <>Envie um <b>.zip</b> com os documentos em <b>PDF</b>. A IA lê cada arquivo e preenche automaticamente os campos deste módulo (datas, nomes, validade, etc.).</>
            ) : (
              <>Envie um <b>.zip</b> com vários documentos. Cada arquivo vira um registro
              {isIso ? " no módulo ISO 14001" : isDoc ? " na biblioteca GED" : " neste módulo"}, com o arquivo já anexado.
              {mapMode && " Depois de ler o ZIP, aponte cada arquivo a um item do checklist."}</>
            )}
          </p>
          {isDoc && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Pasta destino</Label>
                <Select value={folder} onValueChange={setFolder}><SelectTrigger data-testid={`${testid}-folder`}><SelectValue /></SelectTrigger><SelectContent>{folderList.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold text-slate-600">Norma</Label>
                <Select value={norm} onValueChange={setNorm}><SelectTrigger data-testid={`${testid}-norm`}><SelectValue /></SelectTrigger><SelectContent>{NORMS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          )}

          {mapMode && entries.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-600">{entries.length} arquivo(s) — aponte cada um a um item da ISO 14001 (ou deixe como novo registro):</p>
              <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                {entries.map((n) => (
                  <div key={n} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 p-2" data-testid={`map-row-${n}`}>
                    <span className="flex-1 truncate text-xs font-medium text-slate-700" title={n}>{n}</span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <Select value={mapping[n] || "__new__"} onValueChange={(v) => setMapping((p) => ({ ...p, [n]: v === "__new__" ? "" : v }))}>
                      <SelectTrigger className="h-8 w-[260px] text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        <SelectItem value="__new__">— Novo registro na ISO —</SelectItem>
                        {isoItems.map((it) => <SelectItem key={it.id} value={it.id}>{it.clause ? `${it.clause} · ` : ""}{it.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={reset}>Trocar arquivo</Button>
                <Button onClick={() => doUpload(pending, mapping)} disabled={busy} className="bg-[#0B2545] hover:bg-[#134074]" data-testid={`${testid}-confirm`}>{busy ? "Importando..." : "Importar e vincular"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div
              onClick={() => !busy && inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleZip(e.dataTransfer.files?.[0]); }}
              className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#E2E8F0] bg-slate-50/50 p-8 text-center hover:border-[#134074]"
              data-testid={`${testid}-dropzone`}
            >
              {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#134074]" /> : <UploadCloud className="h-7 w-7 text-[#134074]" />}
              <p className="text-sm font-medium text-slate-600">{busy ? (progress || "Lendo/importando...") : "Arraste o .zip ou clique para selecionar"}</p>
              <p className="text-xs text-slate-400">{aiAso ? "A leitura com IA pode levar alguns minutos para muitos documentos" : "Aceita PDF, imagens, DOCX, XLSX dentro do ZIP"}</p>
              <input ref={inputRef} type="file" accept=".zip" className="hidden" onChange={(e) => handleZip(e.target.files?.[0])} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
